import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform, useSpring, useMotionValue, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, Store, ChefHat, Tractor, Sprout, PawPrint } from "lucide-react";
import { ProductCard } from "../components/ProductCard";
import { MarqueeRibbon } from "../components/Marquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMG = {
  hero: "https://customer-assets-lqy194kg.emergentagent.net/job_garlic-luxury-1/artifacts/0i5g4snx_20250806_103248.webp",
  family: "https://customer-assets-lqy194kg.emergentagent.net/job_garlic-luxury-1/artifacts/z34rovia_IMG-20260323-WA0020.webp",
  b2b: "https://customer-assets-lqy194kg.emergentagent.net/job_garlic-luxury-1/artifacts/96jhtgaz_20250806_111223.webp",
};

const RevealLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-2 -mb-2">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Chapter = ({ num, title }) => (
  <div className="flex items-end gap-6 mb-6">
    <span className="font-display text-7xl lg:text-8xl leading-[0.8] text-outline select-none">{num}</span>
    <p className="overline-tag mb-2">{title}</p>
  </div>
);

const Intro = () => (
  <motion.div
    className="fixed inset-0 z-[70] bg-obsidian flex items-center justify-center"
    exit={{ y: "-100%" }}
    transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
  >
    <div className="text-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.15 }}
      >
        <Sprout className="w-8 h-8 text-saffron mx-auto mb-6" />
      </motion.div>
      <div className="overflow-hidden">
        <motion.p
          initial={{ y: "110%" }}
          animate={{ y: 0 }}
          transition={{ duration: 0.8, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-4xl sm:text-5xl text-zinc-100 tracking-tight"
        >
          The Garlic <span className="italic text-saffron">Hog</span>
        </motion.p>
      </div>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.65 }}
        className="overline-tag mt-5"
      >
        Mount Albert · Ontario
      </motion.p>
    </div>
  </motion.div>
);

const HERO_DELAY = 1.5;

const Hero = () => {
  const ref = useRef(null);
  const [intro, setIntro] = useState(true);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });

  useEffect(() => {
    const t = setTimeout(() => setIntro(false), 1400);
    return () => clearTimeout(t);
  }, []);

  const onMouse = (e) => {
    mx.set((e.clientX / window.innerWidth - 0.5) * 28);
    my.set((e.clientY / window.innerHeight - 0.5) * 18);
  };

  return (
    <section ref={ref} onMouseMove={onMouse} className="relative min-h-screen flex items-end overflow-hidden">
      <AnimatePresence>{intro && <Intro />}</AnimatePresence>
      <motion.div style={{ x: sx, y: sy }} className="absolute -inset-8">
        <motion.img
          src={IMG.hero}
          alt="Freshly harvested hardneck garlic from The Garlic Hog farm"
          style={{ y }}
          className="w-full h-[120%] object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/80 via-[#0A0A0A]/25 to-transparent" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-28 pt-48 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: HERO_DELAY }}
          className="overline-tag mb-6"
        >
          Mount Albert, Ontario — A Real Family Farm
        </motion.p>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light text-zinc-100 max-w-4xl leading-[1.02]">
          <RevealLine delay={HERO_DELAY + 0.1}>Nature's abundant</RevealLine>
          <RevealLine delay={HERO_DELAY + 0.22}>
            <span className="italic text-saffron">luxury</span>, grown by
          </RevealLine>
          <RevealLine delay={HERO_DELAY + 0.34}>stubborn hands.</RevealLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: HERO_DELAY + 0.55 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300"
        >
          Hardneck Asiatic garlic and semi-exotic vegetables, homegrown organic and pulled from dark
          Ontario soil. Down to earth. Unreasonably good.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: HERO_DELAY + 0.7 }}
          className="mt-12 flex flex-wrap gap-4"
        >
          <Link
            to="/shop"
            data-testid="hero-shop-cta"
            className="group flex items-center gap-3 rounded-full bg-saffron px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold text-obsidian hover:bg-saffron-hover transition-colors focus-ring"
          >
            Pre-order the Harvest
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/tours"
            data-testid="hero-tour-cta"
            className="flex items-center gap-3 rounded-full border border-zinc-500 px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold text-zinc-200 hover:border-saffron hover:text-saffron transition-colors focus-ring"
          >
            Book a Farm Tour
          </Link>
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: HERO_DELAY + 1 }}
        className="absolute bottom-10 right-10 z-10 hidden sm:flex flex-col items-center gap-4"
      >
        <span className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 [writing-mode:vertical-rl]">
          Scroll
        </span>
        <div className="w-px h-16 bg-line overflow-hidden">
          <motion.div
            className="w-full h-1/2 bg-saffron"
            animate={{ y: ["-100%", "220%"] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
};

const Story = () => (
  <section className="py-32">
    <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
      <motion.div
        initial={{ opacity: 0, x: -32 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="lg:col-span-5"
      >
        <p className="overline-tag mb-6">Who We Are</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-zinc-100 leading-tight">
          A family that talks to <span className="italic text-saffron">garlic</span>.
        </h2>
        <p className="mt-8 text-base leading-relaxed text-zinc-300">
          We're a small, stubborn family operation out of Mount Albert, Ontario. No warehouses, no
          middlemen, no mystery — just rows of hardneck Asiatic garlic, purple-podded beans, burgundy
          okra, and tomatoes that taste like tomatoes used to.
        </p>
        <p className="mt-6 text-base leading-relaxed text-zinc-400">
          We grow slow, harvest by hand, and sell at the farm gate and to a handful of chefs who care
          as much as we do. Good food is nature's luxury — we just keep it honest.
        </p>
        <Link
          to="/gallery"
          data-testid="story-gallery-link"
          className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-saffron hover:text-saffron-hover transition-colors focus-ring"
        >
          Step into the field <ArrowUpRight className="w-4 h-4" />
        </Link>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: 32 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.15 }}
        className="lg:col-span-7"
      >
        <div className="overflow-hidden border border-line">
          <img
            src={IMG.family}
            alt="The family working the farm in Mount Albert"
            loading="lazy"
            className="w-full max-h-[70vh] object-cover hover:scale-105 transition-transform duration-700"
          />
        </div>
        <p className="mt-4 text-xs uppercase tracking-[0.2em] text-zinc-600">
          Mud on the boots, saffron in the soil — Mount Albert, ON
        </p>
      </motion.div>
    </div>

    <div className="max-w-7xl mx-auto px-6 mt-20">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        data-testid="harley-cameo"
        className="border border-line bg-surface p-8 lg:p-12 flex flex-col sm:flex-row items-center gap-10"
      >
        <div className="overflow-hidden rounded-t-[8rem] border border-line w-56 shrink-0">
          <img
            src="/images/harley-harvest.webp"
            alt="Harley the German Shepherd proudly guarding a pile of freshly pulled garlic"
            className="w-full object-cover hover:scale-105 transition-transform duration-700"
          />
        </div>
        <div>
          <p className="overline-tag mb-4 flex items-center gap-2">
            <PawPrint className="w-4 h-4" /> Head of Harvest Security
          </p>
          <h3 className="font-display text-2xl sm:text-3xl tracking-tight text-zinc-100">
            And then there's <span className="italic text-saffron">Harley</span>.
          </h3>
          <p className="mt-4 text-sm text-zinc-400 leading-relaxed max-w-2xl">
            A German Shepherd born and raised on the farm — he's never known a day without mud and
            wouldn't want one. Harley runs wild, escorts every farm tour, and supervises each harvest
            from exactly where the shade is. He takes full credit for the garlic. We let him.
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);

const GarlicFocus = () => (
  <section className="relative py-32 overflow-hidden">
    <img src={IMG.b2b} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-25" />
    <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-transparent to-[#0A0A0A]" />
    <div className="relative z-10 max-w-7xl mx-auto px-6">
      <Chapter num="02" title="The Hog's Obsession" />
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-zinc-100 max-w-2xl leading-tight">
        Garlic first. <span className="italic text-wine-light">Everything else follows.</span>
      </h2>
      <p className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300">
        Our mixed-variety hardneck Asiatic garlic is the core of the farm — bold, complex, and grown
        for people who cook like they mean it. Two ways to bring it home.
      </p>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="border border-line bg-surface/80 backdrop-blur-md p-10 flex flex-col gap-5 hover:shadow-[0_10px_40px_-10px_rgba(212,141,59,0.15)] transition-shadow duration-500"
        >
          <Store className="w-8 h-8 text-saffron" />
          <h3 className="font-display text-2xl sm:text-3xl text-zinc-100">For Your Kitchen</h3>
          <p className="text-sm text-zinc-400 leading-relaxed">
            Pre-order by the pound and pick up at the farm gate. Fresh-cured, never refrigerated,
            varieties rotating with the season.
          </p>
          <Link
            to="/shop"
            data-testid="b2c-shop-cta"
            className="mt-auto inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-saffron hover:text-saffron-hover transition-colors focus-ring w-fit"
          >
            Pre-order garlic <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="border border-line bg-forest/40 backdrop-blur-md p-10 flex flex-col gap-5 hover:shadow-[0_10px_40px_-10px_rgba(212,141,59,0.15)] transition-shadow duration-500"
        >
          <ChefHat className="w-8 h-8 text-saffron" />
          <h3 className="font-display text-2xl sm:text-3xl text-zinc-100">For Chefs &amp; Retailers</h3>
          <p className="text-sm text-zinc-400 leading-relaxed">
            B2B wholesale on hardneck garlic and our semi-exotic lines. Standing orders, chef's-table
            tastings, and first pick of the rare beds.
          </p>
          <a
            href="mailto:thegarlichog@gmail.com?subject=Wholesale%20Enquiry"
            data-testid="b2b-wholesale-cta"
            className="mt-auto inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-saffron hover:text-saffron-hover transition-colors focus-ring w-fit"
          >
            Start a wholesale conversation <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </div>
  </section>
);

const Harvest = ({ products }) => (
  <section className="py-32">
    <div className="max-w-7xl mx-auto px-6">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
        <div>
          <Chapter num="03" title="This Season" />
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-zinc-100">
            The harvest board
          </h2>
        </div>
        <Link
          to="/shop"
          data-testid="harvest-view-all-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-saffron hover:text-saffron-hover transition-colors focus-ring"
        >
          View the full harvest <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {products.slice(0, 6).map((p, i) => (
          <ProductCard key={p.id} product={p} index={i} large={i === 0} />
        ))}
      </div>
    </div>
  </section>
);

const TourCta = () => (
  <section className="py-32 border-t border-line bg-wine-deep/30">
    <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-8">
        <Chapter num="04" title="Come See It Growing" />
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-zinc-100 leading-tight">
          Walk the rows. Pull a bulb. <span className="italic text-saffron">Taste the dirt's work.</span>
        </h2>
        <p className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400">
          Our small-group farm tours wander the garlic beds, the bean trellises, and the okra patch —
          finishing with a tasting of whatever the season is showing off.
        </p>
      </div>
      <div className="lg:col-span-4 flex lg:justify-end">
        <Link
          to="/tours"
          data-testid="tour-cta-button"
          className="group flex items-center gap-3 rounded-full bg-saffron px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold text-obsidian hover:bg-saffron-hover transition-colors focus-ring"
        >
          <Tractor className="w-4 h-4" />
          Book Your Tour
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  </section>
);

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <Hero />
      <MarqueeRibbon />
      <Story />
      <GarlicFocus />
      <Harvest products={products} />
      <TourCta />
    </main>
  );
}
